import { type DashboardSection, type DashboardWidget, isPinnedSection } from '@adtrackify/at-tracking-event-types';
import { type Layout } from 'react-grid-layout';
import type { DashboardWidgetLayoutWithOrdinal } from '../types/overview-types';

;

interface LegacyOrderEntry {
  widget: DashboardWidget;
  originalIndex: number;
}

interface CanonicalOrderEntry extends LegacyOrderEntry {
  legacyRank: number;
  ordinal?: number;
}

export function getDashboardWidgetOrdinal(widget: DashboardWidget): number | undefined {
  const layout = widget.layout as DashboardWidgetLayoutWithOrdinal;
  const ordinal = layout.ordinal;
  if (ordinal === undefined || !Number.isFinite(ordinal)) return undefined;
  return ordinal;
}

export function sortWidgetsByLegacyVisualOrder(widgets: DashboardWidget[]): DashboardWidget[] {
  return widgets
    .map((widget, originalIndex): LegacyOrderEntry => ({ widget, originalIndex }))
    .sort(compareLegacyOrderEntries)
    .map(entry => entry.widget);
}

export function sortWidgetsByCanonicalOrder(widgets: DashboardWidget[]): DashboardWidget[] {
  if (widgets.length === 0) return widgets;

  const legacyRankById = buildLegacyRankMap(widgets);
  const maxOrdinal = getMaxOrdinal(widgets);
  const missingOrdinalBase = maxOrdinal + 1;

  return widgets
    .map((widget, originalIndex): CanonicalOrderEntry => ({
      widget,
      originalIndex,
      legacyRank: legacyRankById.get(widget.id) ?? originalIndex,
      ordinal: getDashboardWidgetOrdinal(widget),
    }))
    .sort((a, b) => compareCanonicalOrderEntries(a, b, missingOrdinalBase))
    .map(entry => entry.widget);
}

export function getCanonicalWidgetOrderIds(widgets: DashboardWidget[]): string[] {
  return sortWidgetsByCanonicalOrder(widgets).map(widget => widget.id);
}

export function getRowMajorLayoutOrderIds(layoutItems: Layout[]): string[] {
  return [...layoutItems]
    .sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      if (a.x !== b.x) return a.x - b.x;
      return a.i.localeCompare(b.i);
    })
    .map(item => item.i);
}

export function sortWidgetsByLayoutPosition(
  widgets: DashboardWidget[],
  layoutItems: Layout[]
): DashboardWidget[] {
  const layoutById = new Map(layoutItems.map(item => [item.i, item]));

  return [...widgets].sort((a, b) => {
    const layoutA = layoutById.get(a.id);
    const layoutB = layoutById.get(b.id);

    if (!layoutA || !layoutB) return 0;
    if (layoutA.y !== layoutB.y) return layoutA.y - layoutB.y;
    return layoutA.x - layoutB.x;
  });
}

export function mergeOrdinalsIntoWidgets(
  widgets: DashboardWidget[],
  orderedWidgetIds: string[]
): DashboardWidget[] {
  const ordinalById = new Map(orderedWidgetIds.map((id, ordinal) => [id, ordinal]));

  return widgets.map(widget => {
    const ordinal = ordinalById.get(widget.id);
    if (ordinal === undefined) return widget;

    return {
      ...widget,
      layout: {
        ...widget.layout,
        ordinal,
      } as DashboardWidgetLayoutWithOrdinal,
    };
  });
}

export function mergeLayoutAndOrdinalsIntoWidgets(
  widgets: DashboardWidget[],
  layoutItems: Layout[]
): DashboardWidget[] {
  const layoutById = new Map(layoutItems.map(item => [item.i, item]));
  const ordinalById = new Map(
    getRowMajorLayoutOrderIds(layoutItems).map((id, ordinal) => [id, ordinal])
  );

  return widgets.map(widget => {
    const item = layoutById.get(widget.id);
    if (!item) return widget;

    return {
      ...widget,
      layout: {
        ...widget.layout,
        ordinal: ordinalById.get(widget.id),
        x: item.x,
        y: item.y,
        columns: item.w,
        rows: item.h,
        minColumns: item.minW ?? widget.layout.minColumns,
        maxColumns: item.maxW ?? widget.layout.maxColumns,
        minRows: item.minH ?? widget.layout.minRows,
        maxRows: item.maxH ?? widget.layout.maxRows,
      } as DashboardWidgetLayoutWithOrdinal,
    };
  });
}

export function mergeLayoutIntoWidgetsForPersistence(
  widgets: DashboardWidget[],
  layoutItems: Layout[]
): DashboardWidget[] {
  const layoutById = new Map(layoutItems.map(item => [item.i, item]));
  const ordinalById = new Map(
    getRowMajorLayoutOrderIds(layoutItems).map((id, ordinal) => [id, ordinal])
  );

  return widgets.map(widget => {
    const item = layoutById.get(widget.id);
    if (!item) return widget;

    return {
      ...widget,
      layout: {
        ...widget.layout,
        x: item.x,
        y: item.y,
        columns: item.w,
        rows: item.h,
        minColumns: item.minW ?? widget.layout.minColumns,
        maxColumns: item.maxW ?? widget.layout.maxColumns,
        minRows: item.minH ?? widget.layout.minRows,
        maxRows: item.maxH ?? widget.layout.maxRows,
        ordinal: ordinalById.get(widget.id),
        // Clear customDimensions - it was incorrectly being set on every save,
        // breaking tier-based sizing. Density-derived dimensions are used instead.
        customDimensions: undefined,
        isDesktopAuthored: true,
      },
    };
  });
}

function compareLegacyOrderEntries(a: LegacyOrderEntry, b: LegacyOrderEntry): number {
  const ay = a.widget.layout?.y ?? 0;
  const by = b.widget.layout?.y ?? 0;
  if (ay !== by) return ay - by;

  const ax = a.widget.layout?.x ?? 0;
  const bx = b.widget.layout?.x ?? 0;
  if (ax !== bx) return ax - bx;

  return a.originalIndex - b.originalIndex;
}

function compareCanonicalOrderEntries(
  a: CanonicalOrderEntry,
  b: CanonicalOrderEntry,
  missingOrdinalBase: number
): number {
  const aOrder = a.ordinal ?? missingOrdinalBase + a.legacyRank;
  const bOrder = b.ordinal ?? missingOrdinalBase + b.legacyRank;

  if (aOrder !== bOrder) return aOrder - bOrder;
  return a.legacyRank - b.legacyRank;
}

function buildLegacyRankMap(widgets: DashboardWidget[]): Map<string, number> {
  const rankById = new Map<string, number>();
  sortWidgetsByLegacyVisualOrder(widgets).forEach((widget, rank) => {
    rankById.set(widget.id, rank);
  });
  return rankById;
}

function getMaxOrdinal(widgets: DashboardWidget[]): number {
  let maxOrdinal = -1;

  for (const widget of widgets) {
    const ordinal = getDashboardWidgetOrdinal(widget);
    if (ordinal !== undefined && ordinal > maxOrdinal) {
      maxOrdinal = ordinal;
    }
  }

  return maxOrdinal;
}

// ============================================
// SECTION ORDINAL HELPERS
// ============================================

export function getNextSectionOrdinal(sections: DashboardSection[]): number {
  if (sections.length === 0) return 0;
  const maxOrdinal = Math.max(...sections.map(s => s.ordinal));
  return maxOrdinal + 1;
}

export function normalizeSectionOrdinals(sections: DashboardSection[]): DashboardSection[] {
  // Separate pinned and regular sections
  const pinned = sections.filter(s => isPinnedSection(s));
  const regular = sections.filter(s => !isPinnedSection(s));

  // Pinned sections get ordinal -1, regular sections get 0, 1, 2, ...
  const normalizedPinned = pinned.map(section => ({
    ...section,
    ordinal: -1,
  }));

  const normalizedRegular = regular.map((section, index) => ({
    ...section,
    ordinal: index,
  }));

  // Pinned sections always come first
  return [...normalizedPinned, ...normalizedRegular];
}

export function insertSectionAtPosition(
  sections: DashboardSection[],
  newSection: DashboardSection,
  position: 'top' | 'bottom' | { after: string }
): DashboardSection[] {
  let targetOrdinal: number;

  if (position === 'top') {
    targetOrdinal = 0;
  } else if (position === 'bottom') {
    targetOrdinal = getNextSectionOrdinal(sections);
  } else {
    // Insert after a specific section
    const afterSection = sections.find(s => s.id === position.after);
    targetOrdinal = afterSection ? afterSection.ordinal + 1 : getNextSectionOrdinal(sections);
  }

  // Shift existing sections that are at or after the target position
  const shiftedSections = sections.map(section => {
    if (section.ordinal >= targetOrdinal) {
      return { ...section, ordinal: section.ordinal + 1 };
    }
    return section;
  });

  // Add the new section at the target ordinal
  const sectionWithOrdinal = { ...newSection, ordinal: targetOrdinal };

  return normalizeSectionOrdinals([...shiftedSections, sectionWithOrdinal]);
}
